// Copyright (c) Docugami, Inc. All rights reserved.

import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { TokenRange } from "networking/api";
import { ChunkRenderComponent, createChunkRenderComponent } from "sdv/components/ChunkRenderComponent";
import { ColorComponent } from "sdv/components/ColorComponent";
import { CursorComponent } from "sdv/components/CursorComponent";
import { DragComponent } from "sdv/components/DragManipulationComponent";
import { FocusViewComponent } from "sdv/components/FocusViewComponent";
import { Grabber, GrabberComponent } from "sdv/components/GrabberComponent";
import { HighlighterComponent } from "sdv/components/HighlighterComponent";
import { InteractableComponent, InteractionType } from "sdv/components/InteractableComponent";
import { MultiSelectComponent } from "sdv/components/MultiSelectComponent";
import { PreviewChunkComponent } from "sdv/components/PreviewChunkComponent";
import { SnapToTokenComponent } from "sdv/components/SnapToTokenComponent";
import { TokenPositionComponent } from "sdv/components/TokenPositionComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { TokenRangeInfoComponent } from "sdv/components/TokenRangeInfoComponent";
import { Chunk } from "store/dgml/dgmlTypes";
import { SpanId } from "utils/SpanIdUtils";
import { ComponentId, tagComponent } from "./Component";
import { Entity, IEntityManager } from "./Entity";

export function createGrabberEntity(
    entityManager: IEntityManager,
    grabber: Grabber,
    onInteractEnd?: (e: Entity) => void): Entity {
    return entityManager.create()
        .addComponent<InteractableComponent>({
            typeId: ComponentId.Interactable,
            interactionType: InteractionType.Drag,
            hitBox: grabber.bounds,
            onInteractEnd: onInteractEnd
        })
        .addComponent<GrabberComponent>({ typeId: ComponentId.Grabber, grabber: grabber })
}

export function createCursor(entityManager: IEntityManager): Entity {
    return entityManager.create()
        .addComponent<CursorComponent>({
            typeId: ComponentId.Cursor
        });
}

export function createFocusViewEntity(entityManager: IEntityManager, tokenRange: TokenRange, scrollToTop?: boolean) {
    return entityManager.create()
        .addComponent<FocusViewComponent>({
            typeId: ComponentId.FocusView,
            scrollToTop: scrollToTop
        })
        .addComponent<TokenRangeComponent>({
            typeId: ComponentId.TokenRange,
            tokenRange: tokenRange
        });
}

export function createHighlightEntity(
    entityManager: IEntityManager) {
    return entityManager.create()
        .addComponent<HighlighterComponent>({
            typeId: ComponentId.Highlighter
        });
}

export function createActiveHighlightEntity(
    entityManager: IEntityManager) {
    return entityManager.create()
        .addComponent<DragComponent>({
            typeId: ComponentId.Drag
        })
        .addComponent<TokenPositionComponent>({
            typeId: ComponentId.TokenPosition
        })
        .addComponent<SnapToTokenComponent>({
            typeId: ComponentId.SnapToToken
        })
        .addComponent<HighlighterComponent>({
            typeId: ComponentId.Highlighter
        });
}

export function createSelectedSpanEntity(entityManager: IEntityManager, folderId: string, spanId: SpanId) {
    const chunk = DgmlInstancesSingleton.getInstanceById(folderId, spanId.value);
    if (!chunk) {
        throw new Error("Failed to create chunk from span");
    }

    return entityManager.create(spanId.value)
        .addComponent<TokenRangeComponent>({
            typeId: ComponentId.TokenRange,
            tokenRange: chunk.tokenRange!
        })
        .addComponent<TokenRangeInfoComponent>({
            typeId: ComponentId.TokenRangeInfo
        })
        .addComponent<ChunkRenderComponent>(createChunkRenderComponent())
        .addComponent(tagComponent(ComponentId.EphemeralSelection));
}

export function createPreviewChunkEntity(entityManager: IEntityManager, chunk: Chunk, color: string): Entity {
    return entityManager.create()
        .addComponent<TokenRangeComponent>({
            typeId: ComponentId.TokenRange,
            tokenRange: chunk.tokenRange!
        })
        .addComponent<TokenRangeInfoComponent>({
            typeId: ComponentId.TokenRangeInfo
        })
        .addComponent<PreviewChunkComponent>({
            typeId: ComponentId.PreviewChunk,
            spanId: chunk.spanId
        })
        .addComponent(tagComponent(ComponentId.Renderable))
        .addComponent<ColorComponent>({
            typeId: ComponentId.Color,
            color: color
        })
        .ensureComponent(createChunkRenderComponent());
}

export function createMultiSelectChunkEntity(entityManager: IEntityManager, spanId: string, tokenRange: TokenRange): Entity {
    return entityManager.create()
        .addComponent<MultiSelectComponent>({
            typeId: ComponentId.MultiSelect,
            spanId: spanId
        })
        .addComponent<TokenRangeComponent>({
            typeId: ComponentId.TokenRange,
            tokenRange: tokenRange
        })
        .addComponent<TokenRangeInfoComponent>({
            typeId: ComponentId.TokenRangeInfo,
        })
        .addComponent(tagComponent(ComponentId.Renderable))
        .ensureComponent(createChunkRenderComponent());
}

const multiSelectComponents = [ComponentId.MultiSelect, ComponentId.TokenRange, ComponentId.TokenRangeInfo, ComponentId.Bounds, ComponentId.ChunkRender, ComponentId.TableChunkRender, ComponentId.TableColumnRender];
export function copyMultiSelectEntity(entityManager: IEntityManager, spanId: string, tokenRange: TokenRange, copyEntity: Entity): Entity {
    const entity = createMultiSelectChunkEntity(entityManager, spanId, tokenRange);
    for (const componentId of multiSelectComponents) {
        if (copyEntity.hasComponent(componentId)) {
            entity.ensureComponent(copyEntity.getComponent(componentId)!, true);
        }
    }

    return entity;
}
